import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import api from '../axios';

export const fetchUsage = createAsyncThunk('usage/fetch', async (_, { rejectWithValue }) => {
  try {
    const { data } = await api.get('/usage');
    return data;
  } catch (e) {
    return rejectWithValue(e.response?.data?.message || 'Failed to load usage');
  }
});

export const fetchBilling = createAsyncThunk('usage/billing', async (_, { rejectWithValue }) => {
  try {
    const { data } = await api.get('/billing/subscription');
    return data;
  } catch (e) {
    return rejectWithValue(e.response?.data?.message || 'Failed to load billing');
  }
});

export const fetchSubscription = createAsyncThunk('usage/subscription', async (_, { rejectWithValue }) => {
  try {
    const { data } = await api.get('/usage/subscription');
    return data.subscription;
  } catch (e) {
    return rejectWithValue(e.response?.data?.message || 'Failed to load subscription');
  }
});

export const fetchActivity = createAsyncThunk('usage/activity', async (_, { rejectWithValue }) => {
  try {
    const { data } = await api.get('/usage/activity?limit=20');
    return data.activity || [];
  } catch (e) {
    return rejectWithValue(e.response?.data?.message || 'Failed to load activity');
  }
});

export const upgradePlan = createAsyncThunk('usage/upgrade', async (tier, { rejectWithValue }) => {
  try {
    const { data } = await api.post('/billing/upgrade', { tier });
    return data.subscription;
  } catch (e) {
    return rejectWithValue(e.response?.data?.message || 'Upgrade failed');
  }
});

export const initTopup = createAsyncThunk('usage/topup', async ({ amountNgn, provider = 'paystack' }, { rejectWithValue }) => {
  try {
    const { data } = await api.post('/billing/topup', { amountNgn, amountKobo: amountNgn * 100, provider });
    return data;
  } catch (e) {
    return rejectWithValue(e.response?.data?.message || 'Top-up failed');
  }
});

export const initPlanPayment = createAsyncThunk('usage/planPayment', async ({ tier }, { rejectWithValue }) => {
  try {
    const { data } = await api.post('/billing/plan-payment', { tier });
    return data;
  } catch (e) {
    return rejectWithValue(e.response?.data?.message || 'Payment initialization failed');
  }
});

const usageSlice = createSlice({
  name: 'usage',
  initialState: {
    tokens: null,
    spend: null,
    latency: null,
    history: [],
    billing: null,
    subscription: null,
    activity: [],
    loading: false,
    subLoading: false,
    error: null,
    lastFetched: null,
  },
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchUsage.pending, (state) => { state.loading = true; state.error = null; })
      .addCase(fetchUsage.fulfilled, (state, { payload }) => {
        state.loading = false;
        const s = payload.summary || {};
        state.tokens = s.totalTokens ?? null;
        state.spend = s.totalSpend ?? null;
        state.latency = s.avgLatencyMs != null ? Math.round(s.avgLatencyMs) : null;
        state.history = payload.dailyHistory || [];
        state.lastFetched = Date.now();
      })
      .addCase(fetchUsage.rejected, (state, { payload }) => { state.loading = false; state.error = payload; })
      .addCase(fetchBilling.fulfilled, (state, { payload }) => { state.billing = payload; })
      .addCase(fetchSubscription.pending, (state) => { state.subLoading = true; })
      .addCase(fetchSubscription.fulfilled, (state, { payload }) => {
        state.subLoading = false;
        state.subscription = payload;
      })
      .addCase(fetchSubscription.rejected, (state) => { state.subLoading = false; })
      .addCase(upgradePlan.fulfilled, (state, { payload }) => { state.subscription = payload; })
      .addCase(fetchActivity.fulfilled, (state, { payload }) => { state.activity = payload; });
  },
});

export default usageSlice.reducer;
